import axios from "axios";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

// Returns "unsupported" | "granted" | "denied" | "default"
export function getNotificationPermission() {
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return "unsupported";
  }
  return Notification.permission;
}

export async function registerPushSubscription(orgId) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return;
  }

  if (!orgId) {
    return;
  }

  if (Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();

    if (permission !== "granted") {
      return;
    }
  }

  const registration = await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const json = subscription.toJSON();

  await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/push/subscribe?organizationId=${orgId}`,
    {
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      organizationId: orgId,
    },
    {
      headers: {
        Authorization: `Bearer ${JSON.parse(localStorage.getItem("user"))?.accessToken}`,
      },
    },
  );
}

export async function getPushSubscriptionStatus() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return false;
  }

  if (Notification.permission !== "granted") {
    return false;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  return !!subscription;
}

export async function enablePushNotifications(orgId) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Push notifications are not supported.");
  }

  if (Notification.permission === "denied") {
    throw new Error(
      "Notifications are blocked in your browser. Please enable them from browser settings.",
    );
  }

  if (Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();

    if (permission !== "granted") {
      return false;
    }
  }

  const registration = await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const json = subscription.toJSON();

  await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/push/subscribe?organizationId=${orgId}`,
    {
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      organizationId: orgId,
    },
    {
      headers: {
        Authorization: `Bearer ${JSON.parse(localStorage.getItem("user"))?.accessToken}`,
      },
    },
  );

  return true;
}

// Subscribes the current device to push notifications for every org the
// user belongs to, using a single browser permission prompt / subscription.
// Returns { granted: boolean, orgIds: string[] } - orgIds is the list that
// was successfully subscribed.
export async function subscribeToAllOrganizations(orgIds = []) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { granted: false, orgIds: [] };
  }

  if (Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { granted: false, orgIds: [] };
    }
  }

  const registration = await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const json = subscription.toJSON();
  const succeeded = [];

  await Promise.all(
    orgIds.filter(Boolean).map(async (orgId) => {
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/push/subscribe?organizationId=${orgId}`,
          {
            endpoint: json.endpoint,
            p256dh: json.keys.p256dh,
            auth: json.keys.auth,
            organizationId: orgId,
          },
          {
            headers: {
              Authorization: `Bearer ${JSON.parse(localStorage.getItem("user"))?.accessToken}`,
            },
          },
        );
        succeeded.push(orgId);
      } catch (err) {
        console.log(`Push subscription failed for org ${orgId}:`, err);
      }
    }),
  );

  return { granted: true, orgIds: succeeded };
}

export async function disablePushNotifications(orgId) {
  const registration = await navigator.serviceWorker.ready;

  const subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    return;
  }

  const endpoint = subscription.endpoint;

  await axios.delete(
    `${process.env.REACT_APP_BACKEND_URL}/push/unsubscribe?organizationId=${orgId}&endpoint=${encodeURIComponent(endpoint)}`,
    {
      headers: {
        Authorization: `Bearer ${JSON.parse(localStorage.getItem("user"))?.accessToken}`,
      },
    },
  );

  await subscription.unsubscribe();
}

// Unsubscribes this device from push notifications for every org passed in,
// then removes the single underlying browser push subscription. Scoped to
// this device's endpoint only - other devices' subscriptions are untouched.
export async function disablePushNotificationsForOrgs(orgIds = []) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (!subscription) return;

  const endpoint = subscription.endpoint;

  await Promise.all(
    orgIds.filter(Boolean).map((orgId) =>
      axios
        .delete(
          `${process.env.REACT_APP_BACKEND_URL}/push/unsubscribe?organizationId=${orgId}&endpoint=${encodeURIComponent(endpoint)}`,
          {
            headers: {
              Authorization: `Bearer ${JSON.parse(localStorage.getItem("user"))?.accessToken}`,
            },
          },
        )
        .catch((err) => console.log(`Unsubscribe failed for org ${orgId}:`, err)),
    ),
  );

  await subscription.unsubscribe();
}